import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { supabase } from '@/integrations/supabase/client';
import type { StoredActiveCheckinSession } from '@/lib/ba/activeShiftStorage';
import {
  clearStoredActiveCheckinSession,
  setStoredCheckoutSummary,
} from '@/lib/ba/activeShiftStorage';
import {
  assessGeofencePosition,
  formatCountdownLabel,
  GEOFENCE_BREACH_GRACE_PERIOD_MS,
  GEOFENCE_SAMPLE_INTERVAL_MS,
} from '@/lib/ba/geofence';
import { fetchPromoterCampaignSurveys } from '@/lib/ba/campaignSurveyLinks';
import { calculateDistance, formatDistance, getCurrentPosition } from '@/lib/geolocation';

const supabaseAny = supabase as any;

type MonitorState = {
  status: 'idle' | 'inside' | 'warning' | 'breach' | 'weak_signal' | 'auto_checkout_pending';
  distanceM: number | null;
  accuracyM: number | null;
  radiusM: number | null;
  countdownLabel: string | null;
  message: string | null;
};

const DEFAULT_STATE: MonitorState = {
  status: 'idle',
  distanceM: null,
  accuracyM: null,
  radiusM: null,
  countdownLabel: null,
  message: null,
};

const getStorageKey = (sessionId: string) => `ba-geofence-monitor:${sessionId}`;

type StoredBreachState = {
  warningIssuedAt: string | null;
  breachStartedAt: string | null;
  lastLoggedEvent: 'warning' | 'breach_started' | 'breach_cleared' | null;
};

const readStoredBreachState = (sessionId: string): StoredBreachState => {
  if (typeof window === 'undefined') {
    return {
      warningIssuedAt: null,
      breachStartedAt: null,
      lastLoggedEvent: null,
    };
  }

  try {
    const raw = window.localStorage.getItem(getStorageKey(sessionId));
    if (!raw) {
      return {
        warningIssuedAt: null,
        breachStartedAt: null,
        lastLoggedEvent: null,
      };
    }

    return JSON.parse(raw) as StoredBreachState;
  } catch (error) {
    console.warn('[geofence-monitor] failed to parse stored state', error);
    return {
      warningIssuedAt: null,
      breachStartedAt: null,
      lastLoggedEvent: null,
    };
  }
};

const writeStoredBreachState = (sessionId: string, state: StoredBreachState) => {
  if (typeof window === 'undefined') return;

  try {
    window.localStorage.setItem(getStorageKey(sessionId), JSON.stringify(state));
  } catch (error) {
    console.warn('[geofence-monitor] failed to persist stored state', error);
  }
};

const clearStoredBreachState = (sessionId: string) => {
  if (typeof window === 'undefined') return;

  try {
    window.localStorage.removeItem(getStorageKey(sessionId));
  } catch (error) {
    console.warn('[geofence-monitor] failed to clear stored state', error);
  }
};

const buildWarningMessage = (distanceM: number, radiusM: number) =>
  `You are leaving the activation area. If you exit the allowed radius, you will be automatically checked out in 5 minutes. Please return to site. Currently ${formatDistance(distanceM)} from site; allowed radius ${formatDistance(radiusM)}.`;

const buildWeakSignalMessage = (radiusM: number) =>
  `Your GPS signal is weak. Stay within ${formatDistance(radiusM)} of the activation site while location accuracy improves.`;

const buildAutoCheckoutSummary = async ({
  session,
  checkedOutAt,
  workedMinutes,
  scheduledMinutes,
  varianceMinutes,
}: {
  session: StoredActiveCheckinSession;
  checkedOutAt: string;
  workedMinutes: number;
  scheduledMinutes: number;
  varianceMinutes: number;
}) => {
  let actionHref: string | null = null;

  try {
    const surveys = await fetchPromoterCampaignSurveys({
      campaignId: session.campaignId,
      activeSession: {
        shiftId: session.shiftId,
        storeId: session.storeId,
      },
    });

    const checkoutSurvey = surveys.find((survey) => survey.required_for_checkout);
    if (checkoutSurvey?.id) {
      actionHref = `/ba/checkout-sessions/${session.id}/surveys/${checkoutSurvey.id}?mode=auto-checkout`;
    }
  } catch (error) {
    console.warn('[geofence-monitor] failed to resolve checkout surveys after auto-checkout', error);
  }

  setStoredCheckoutSummary({
    sessionId: session.id,
    campaignId: session.campaignId,
    campaignName: session.campaignName ?? 'Campaign',
    checkedInAt: session.checkedInAt,
    checkedOutAt,
    scheduledMinutes,
    workedMinutes,
    varianceMinutes,
    status: 'auto_closed',
    note: actionHref
      ? 'You were automatically checked out after remaining outside the activation radius. Complete the required checkout survey now.'
      : 'You were automatically checked out after remaining outside the activation radius.',
    actionHref,
    actionLabel: actionHref ? 'Complete checkout survey' : null,
  });
};

export const useShiftGeofenceMonitor = (activeSession: StoredActiveCheckinSession | null) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [state, setState] = useState<MonitorState>(DEFAULT_STATE);
  const breachStateRef = useRef<StoredBreachState | null>(null);
  const sampleInFlightRef = useRef(false);
  const autoCheckoutStartedRef = useRef(false);
  const sessionRef = useRef<StoredActiveCheckinSession | null>(activeSession);

  useEffect(() => {
    sessionRef.current = activeSession;
  }, [activeSession]);

  useEffect(() => {
    if (!activeSession?.id) {
      breachStateRef.current = null;
      autoCheckoutStartedRef.current = false;
      setState(DEFAULT_STATE);
      return;
    }

    breachStateRef.current = readStoredBreachState(activeSession.id);
    autoCheckoutStartedRef.current = false;
  }, [activeSession?.id]);

  const canMonitor = useMemo(
    () =>
      !!activeSession?.id &&
      typeof activeSession.storeLat === 'number' &&
      typeof activeSession.storeLng === 'number' &&
      typeof activeSession.geoRadiusMeters === 'number' &&
      activeSession.geoRadiusMeters > 0,
    [activeSession],
  );

  useEffect(() => {
    if (!activeSession?.id || !canMonitor) {
      return;
    }

    const logEvent = async ({
      eventType,
      lat,
      lng,
      distanceM,
      accuracyM,
      reason,
      meta,
    }: {
      eventType: 'warning' | 'breach_started' | 'breach_cleared';
      lat: number;
      lng: number;
      distanceM: number;
      accuracyM: number | null;
      reason: string;
      meta?: Record<string, unknown>;
    }) => {
      try {
        const { error } = await supabaseAny.rpc('ba_log_geofence_event', {
          p_session_id: activeSession.id,
          p_event_type: eventType,
          p_lat: lat,
          p_lng: lng,
          p_distance_m: Math.round(distanceM),
          p_accuracy_m: accuracyM,
          p_reason: reason,
          p_meta: meta ?? {},
        });

        if (error) throw error;
      } catch (error) {
        console.warn('[geofence-monitor] failed to log geofence event', error);
      }
    };

    const autoCheckout = async ({
      lat,
      lng,
      distanceM,
      accuracyM,
    }: {
      lat: number;
      lng: number;
      distanceM: number;
      accuracyM: number | null;
    }) => {
      if (autoCheckoutStartedRef.current) return;
      autoCheckoutStartedRef.current = true;

      try {
        const checkedOutAt = new Date().toISOString();
        const { data, error } = await supabaseAny.rpc('ba_auto_checkout_due_to_geofence_breach', {
          p_session_id: activeSession.id,
          p_lat: lat,
          p_lng: lng,
          p_distance_m: Math.round(distanceM),
          p_accuracy_m: accuracyM,
          p_device_id: typeof navigator === 'undefined' ? 'browser' : navigator.userAgent.slice(0, 120),
          p_checked_out_at: checkedOutAt,
        });

        if (error) throw error;

        const row = Array.isArray(data) ? data[0] ?? null : data ?? null;
        if (!row?.checkin_session_id) {
          throw new Error('Unable to complete automatic checkout.');
        }

        await buildAutoCheckoutSummary({
          session: activeSession,
          checkedOutAt: row.checked_out_at ?? checkedOutAt,
          workedMinutes: Number(row.worked_minutes ?? 0),
          scheduledMinutes: Number(row.scheduled_minutes ?? 0),
          varianceMinutes: Number(row.variance_minutes ?? 0),
        });

        clearStoredActiveCheckinSession();
        clearStoredBreachState(activeSession.id);
        toast.error('You were automatically checked out after leaving the activation area.');
        queryClient.invalidateQueries({ queryKey: ['ba-active-checkin-session'] });
        queryClient.invalidateQueries({ queryKey: ['ba-promoter-workflow'] });
        queryClient.invalidateQueries({ queryKey: ['ba-campaign-visibility'] });
        queryClient.invalidateQueries({ queryKey: ['ba-home'] });
        navigate('/ba/campaigns', { replace: true });
      } catch (error: any) {
        autoCheckoutStartedRef.current = false;
        toast.error(error?.message || 'Automatic checkout failed. Please return to site or check out manually.');
      }
    };

    const samplePosition = async () => {
      if (sampleInFlightRef.current || autoCheckoutStartedRef.current) return;

      sampleInFlightRef.current = true;
      try {
        const position = await getCurrentPosition();
        const currentSession = sessionRef.current;
        if (!currentSession?.id || typeof currentSession.storeLat !== 'number' || typeof currentSession.storeLng !== 'number') {
          return;
        }

        const radiusM = currentSession.geoRadiusMeters ?? null;
        if (!radiusM || radiusM <= 0) return;

        const distanceM = calculateDistance(
          position.lat,
          position.lng,
          currentSession.storeLat,
          currentSession.storeLng,
        );
        const accuracyM =
          typeof position.accuracy === 'number' && Number.isFinite(position.accuracy)
            ? Math.round(position.accuracy)
            : null;
        const assessment = assessGeofencePosition({
          distanceM,
          radiusM,
          accuracyM,
        });
        const breachState = breachStateRef.current ?? {
          warningIssuedAt: null,
          breachStartedAt: null,
          lastLoggedEvent: null,
        };
        const nowIso = new Date().toISOString();

        if (assessment.weakSignal) {
          setState({
            status: 'weak_signal',
            distanceM: Math.round(distanceM),
            accuracyM,
            radiusM,
            countdownLabel: breachState.breachStartedAt
              ? formatCountdownLabel(
                  Math.max(
                    GEOFENCE_BREACH_GRACE_PERIOD_MS -
                      (Date.now() - new Date(breachState.breachStartedAt).getTime()),
                    0,
                  ),
                )
              : null,
            message: buildWeakSignalMessage(radiusM),
          });
          return;
        }

        if (!assessment.outsideWarningRadius) {
          if (breachState.warningIssuedAt || breachState.breachStartedAt) {
            breachStateRef.current = {
              warningIssuedAt: null,
              breachStartedAt: null,
              lastLoggedEvent: 'breach_cleared',
            };
            writeStoredBreachState(currentSession.id, breachStateRef.current);
            void logEvent({
              eventType: 'breach_cleared',
              lat: position.lat,
              lng: position.lng,
              distanceM,
              accuracyM,
              reason: 'Returned inside the activation radius.',
            });
          }

          setState({
            status: 'inside',
            distanceM: Math.round(distanceM),
            accuracyM,
            radiusM,
            countdownLabel: null,
            message: `Within the activation area. ${formatDistance(distanceM)} from site; allowed radius ${formatDistance(radiusM)}.`,
          });
          return;
        }

        if (!breachState.warningIssuedAt) {
          toast.warning(buildWarningMessage(Math.round(distanceM), radiusM), {
            duration: 10_000,
          });
          breachState.warningIssuedAt = nowIso;
          breachState.lastLoggedEvent = 'warning';
          writeStoredBreachState(currentSession.id, breachState);
          breachStateRef.current = breachState;
          void logEvent({
            eventType: 'warning',
            lat: position.lat,
            lng: position.lng,
            distanceM,
            accuracyM,
            reason: 'Promoter moved outside the activation radius.',
          });
        }

        if (assessment.outsideAutoCheckoutRadius) {
          if (!breachState.breachStartedAt) {
            breachState.breachStartedAt = nowIso;
            breachState.lastLoggedEvent = 'breach_started';
            writeStoredBreachState(currentSession.id, breachState);
            breachStateRef.current = breachState;
            void logEvent({
              eventType: 'breach_started',
              lat: position.lat,
              lng: position.lng,
              distanceM,
              accuracyM,
              reason: 'Promoter remained outside the activation radius beyond the GPS drift buffer.',
              meta: {
                warning_issued_at: breachState.warningIssuedAt,
              },
            });
          }

          const remainingMs = Math.max(
            GEOFENCE_BREACH_GRACE_PERIOD_MS -
              (Date.now() - new Date(breachState.breachStartedAt).getTime()),
            0,
          );
          const countdownLabel = formatCountdownLabel(remainingMs);

          setState({
            status: remainingMs > 0 ? 'breach' : 'auto_checkout_pending',
            distanceM: Math.round(distanceM),
            accuracyM,
            radiusM,
            countdownLabel,
            message: `Outside the activation radius. Automatic checkout in ${countdownLabel} unless you return on site.`,
          });

          if (remainingMs <= 0) {
            await autoCheckout({
              lat: position.lat,
              lng: position.lng,
              distanceM,
              accuracyM,
            });
          }
          return;
        }

        setState({
          status: 'warning',
          distanceM: Math.round(distanceM),
          accuracyM,
          radiusM,
          countdownLabel: null,
          message: buildWarningMessage(Math.round(distanceM), radiusM),
        });
      } catch (error) {
        console.warn('[geofence-monitor] failed to sample location', error);
      } finally {
        sampleInFlightRef.current = false;
      }
    };

    void samplePosition();

    const intervalId = window.setInterval(() => {
      void samplePosition();
    }, GEOFENCE_SAMPLE_INTERVAL_MS);

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        void samplePosition();
      }
    };

    const handleFocus = () => {
      void samplePosition();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [activeSession, canMonitor, navigate, queryClient]);

  return {
    ...state,
    enabled: canMonitor,
  };
};
